/**
 * Bootstrap Hook
 * 应用启动时调用 Bootstrap API 获取区域配置和合规信息
 *
 * 功能：
 * - 使用 React Query 调用 Bootstrap API
 * - 实现 localStorage 缓存逻辑（24 小时 TTL）
 * - 支持 locale 参数
 *
 * **Validates: Requirements 1.1**
 */

import { useQuery } from "@tanstack/react-query";
import apiClient from "@/lib/apiClient";
import type { ComplianceMode } from "@/types/operating-region";

/**
 * Bootstrap API 响应数据结构
 */
export interface BootstrapData {
  /** 检测到的区域代码 */
  detected_region: string;
  /** 合规模式 */
  compliance_mode: ComplianceMode;
  /** 默认语言 */
  default_language: string;
  /** 法律实体 */
  legal_entity: string;
  /** 功能开关 */
  features: Record<string, boolean>;
  /** 协议链接 */
  links: {
    terms: string;
    privacy: string;
  };
  /** 检测来源 */
  detection_source: string;
}

/**
 * 缓存配置
 */
const BOOTSTRAP_CACHE_KEY = "bootstrap_data";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 小时

/**
 * 缓存数据结构
 */
interface CachedBootstrapData {
  data: BootstrapData;
  timestamp: number;
  locale?: string;
}

/**
 * 从 localStorage 读取缓存数据
 */
function getCachedData(locale?: string): BootstrapData | undefined {
  try {
    const cached = localStorage.getItem(BOOTSTRAP_CACHE_KEY);
    if (!cached) return undefined;

    const {
      data,
      timestamp,
      locale: cachedLocale,
    } = JSON.parse(cached) as CachedBootstrapData;

    // 检查缓存是否过期
    if (Date.now() - timestamp >= CACHE_TTL) {
      localStorage.removeItem(BOOTSTRAP_CACHE_KEY);
      return undefined;
    }

    // 检查 locale 是否匹配（如果指定了 locale）
    if (locale && cachedLocale !== locale) {
      return undefined;
    }

    return data;
  } catch (e) {
    console.warn("Failed to read bootstrap cache:", e);
    return undefined;
  }
}

/**
 * 保存数据到 localStorage 缓存
 */
function setCachedData(data: BootstrapData, locale?: string): void {
  try {
    const cacheData: CachedBootstrapData = {
      data,
      timestamp: Date.now(),
      locale,
    };
    localStorage.setItem(BOOTSTRAP_CACHE_KEY, JSON.stringify(cacheData));
  } catch (e) {
    console.warn("Failed to save bootstrap cache:", e);
  }
}

/**
 * 清除缓存
 */
export function clearBootstrapCache(): void {
  try {
    localStorage.removeItem(BOOTSTRAP_CACHE_KEY);
  } catch (e) {
    console.warn("Failed to clear bootstrap cache:", e);
  }
}

/**
 * 从 API 获取 Bootstrap 数据
 */
async function fetchBootstrapData(locale?: string): Promise<BootstrapData> {
  const url = locale
    ? `/api/v1/bootstrap?locale=${encodeURIComponent(locale)}`
    : "/api/v1/bootstrap";

  const data = await apiClient<BootstrapData>(url, {
    disableAutoToast: true,
  });

  // 缓存到 localStorage
  setCachedData(data, locale);

  return data;
}

/**
 * useBootstrap Hook
 *
 * 应用启动时调用，获取区域配置和合规信息
 *
 * @param locale - 可选的语言偏好参数
 * @returns React Query 查询结果
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useBootstrap();
 *
 * if (isLoading) return <Loading />;
 * if (error) return <Error />;
 *
 * console.log(data.detected_region); // 'JP'
 * console.log(data.compliance_mode); // 'standard'
 * ```
 */
export function useBootstrap(locale?: string) {
  return useQuery({
    queryKey: ["bootstrap", locale],
    queryFn: () => fetchBootstrapData(locale),
    initialData: () => getCachedData(locale),
    staleTime: CACHE_TTL,
    gcTime: CACHE_TTL,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

export default useBootstrap;
